import { monotonicFactory } from 'ulid';

/**
 * Monotonic ULID factory — see `keyFor.ts` for the rationale (Phase 3 W3-T3
 * task #30 / DECISION-W3-11). sessionCache uses an independent monotonic
 * generator so its keys remain sortable even when the in-memory and
 * persistent caches grow simultaneously within the same millisecond.
 */
const ulid = monotonicFactory();

/**
 * sessionStorage-backed Idempotency-Key cache for flows that span page reloads.
 *
 * Use case: checkout / payments-confirm. TossPayments redirects the user from
 * `/checkout` → external Toss page → `/checkout/success`, which destroys the
 * in-memory Map in `keyFor.ts`. Without a persistent cache, the success page's
 * `POST /payments/confirm` would generate a NEW ULID — and BE would treat the
 * second click (e.g. user refresh) as a separate request instead of a retry.
 *
 * Storage shape: one entry per (method, path, body-hash) cacheKey.
 *   key:   `everline.idempotency.${cacheKey}`
 *   value: `{ key: string, createdAt: number }` (JSON-encoded)
 *
 * TTL: 10 minutes ceiling — must be < BE replay window. Matches the in-memory
 * Map TTL in `keyFor.ts` so behavior is identical regardless of cache tier.
 *
 * SSR-safe: every accessor guards `typeof sessionStorage === 'undefined'`.
 * Next.js RSC may invoke http-core middleware during prefetch where no
 * sessionStorage exists — these paths fall through to in-memory.
 *
 * Resilient: catches QuotaExceededError / SecurityError (private-mode Safari,
 * tracking-protection contexts). On failure the caller still gets a fresh
 * ULID — correctness is preserved, only the persistence guarantee weakens.
 */
const PREFIX = 'everline.idempotency.';
const TEN_MIN_MS = 10 * 60 * 1000;

interface PersistedEntry {
  key: string;
  createdAt: number;
}

const storageAvailable = (): boolean => {
  return typeof sessionStorage !== 'undefined';
};

const readEntry = (cacheKey: string): PersistedEntry | null => {
  if (!storageAvailable()) return null;
  try {
    const raw = sessionStorage.getItem(PREFIX + cacheKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      typeof (parsed as PersistedEntry).key !== 'string' ||
      typeof (parsed as PersistedEntry).createdAt !== 'number'
    ) {
      return null;
    }
    return parsed as PersistedEntry;
  } catch {
    return null;
  }
};

const writeEntry = (cacheKey: string, entry: PersistedEntry): void => {
  if (!storageAvailable()) return;
  try {
    sessionStorage.setItem(PREFIX + cacheKey, JSON.stringify(entry));
  } catch {
    /* quota exceeded or private-mode — caller still receives a valid key */
  }
};

const deleteEntry = (cacheKey: string): void => {
  if (!storageAvailable()) return;
  try {
    sessionStorage.removeItem(PREFIX + cacheKey);
  } catch {
    /* ignore */
  }
};

/**
 * Look up or create a persistent Idempotency-Key for the given cacheKey.
 * Returns the existing key when present and within TTL; otherwise generates
 * a fresh ULID, persists it, and returns it.
 *
 * The caller (typically the http-core middleware) is responsible for
 * computing `cacheKey` as `${method} ${path} ${bodyHash}` so multiple
 * mutations on different endpoints do not collide.
 */
export const lookupOrCreatePersistentKey = (cacheKey: string): string => {
  const now = Date.now();
  const existing = readEntry(cacheKey);
  if (existing && now - existing.createdAt < TEN_MIN_MS) {
    return existing.key;
  }
  const key = ulid();
  writeEntry(cacheKey, { key, createdAt: now });
  return key;
};

/** Remove a persistent entry — invoked from `releaseIdempotencyKey` on success. */
export const releasePersistentKey = (cacheKey: string): void => {
  deleteEntry(cacheKey);
};

/** Test-only: wipe every `everline.idempotency.*` entry from sessionStorage. */
export const __resetPersistentIdempotencyStore = (): void => {
  if (!storageAvailable()) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(PREFIX)) keys.push(k);
    }
    for (const k of keys) sessionStorage.removeItem(k);
  } catch {
    /* ignore */
  }
};
